/**
 * Data plumbing for the unified school print dialog — one hook so the dialog
 * stays presentational. Resolves the preset ranges on the CAMPUS clock (via
 * the service-history payload's timeZone), derives per-day pill options and
 * document counts from the same payload, and builds the /school/print URL.
 *
 * Counts come straight from service-history (record rows + mealtimes per
 * day) — no off-screen document measurement here. Honest counts now beat a
 * page estimate that requires rendering N USDA tables before the dialog is
 * interactive; the artifact page itself still measures for its cover.
 */

import { useQueries } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { useServiceHistoryQuery } from '@/api/cafeteria'
import { ENROLLMENT_FACT_LABELS } from '@/api/family'
import { fetchSchoolForecastBatchesForMonth, useMySchoolAdminLocationsQuery } from '@/api/school-orders'
import { useSchoolPrintRoster } from '@/components/school-print/use-school-print-roster'
import { cafeteriaDeadlinePolicy, ordersConfirmedForDay } from '@/lib/cafeteria/order-window'
import { defaultRosterGrouping } from '@/lib/cafeteria/print-plan'
import { homeroomFacetLabel } from '@/lib/cafeteria/serve-filters'
import type { Mealtime } from '@/lib/mealtime'
import { buildAlreadyOrdered } from '@/lib/school-orders/already-ordered'
import {
  buildSchoolPrintDayOptions,
  buildSchoolPrintMealtimeOptions,
  buildSchoolPrintOrderColumns,
  passoutComposition,
  type SchoolPrintDayOption,
  type SchoolPrintMealtimeOption,
} from '@/lib/school-print/print-plan'
import { campusTodayIso, clampCustomRange, schoolPrintPresets, type SchoolPrintRange } from '@/lib/school-print/range'
import { type SchoolPrintRequest, schoolPrintRequestUrl, type SchoolPrintSectionKey } from '@/lib/school-print/request'

export type SchoolPrintPreset = 'today' | 'thisWeek' | 'nextWeek' | 'custom'

export interface SchoolPrintSections {
  records: boolean
  passout: boolean
  /** Combined school + parent Orders sheet per day. */
  orders: boolean
}

export interface UseSchoolPrintPreviewArgs {
  locationId: string | null
  /** Load the roster choices only while the dialog is visible. */
  open?: boolean
  /** Hide + never offer the records section (kiosk sessions). */
  hideRecordsSection?: boolean
  /**
   * Per-surface starting selection — the ONE dialog stays identical, but a
   * surface may pre-check the section it's about (the cafeteria Orders
   * lookup opens with the Orders sheet on and the rest off).
   */
  defaultSections?: Partial<SchoolPrintSections>
  /**
   * Seed the dialog onto a specific day instead of the `today` preset — a
   * serve PREVIEW passes the previewed date so Print lands on the day being
   * looked at (PROD-1076). Ignored when it IS today (the preset is cleaner).
   */
  initialDay?: string
}

export function useSchoolPrintPreview({
  locationId,
  open = true,
  hideRecordsSection = false,
  defaultSections,
  initialDay,
}: UseSchoolPrintPreviewArgs) {
  const seedCustomDay =
    Boolean(initialDay) && initialDay !== campusTodayIso(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [preset, setPreset] = useState<SchoolPrintPreset>(seedCustomDay ? 'custom' : 'today')
  const [customFrom, setCustomFrom] = useState(seedCustomDay ? initialDay! : '')
  const [customTo, setCustomTo] = useState(seedCustomDay ? initialDay! : '')
  const [excludedDays, setExcludedDays] = useState<ReadonlySet<string>>(new Set())
  // The Mealtimes row (PROD-1279 part two): mealtimes taken OUT of the print.
  // Stored as exclusions, like the day pills, so a mealtime that appears when
  // the range widens is in by default.
  const [excludedMealtimes, setExcludedMealtimes] = useState<ReadonlySet<Mealtime>>(new Set())
  const [sections, setSections] = useState<SchoolPrintSections>({
    records: true,
    passout: true,
    orders: false,
    ...defaultSections,
  })
  // Orders-sheet bucket filter (4f289cdc) — columnIds picked in the dialog.
  // Empty = no filter = print every bucket (the default and the
  // behavior-identical path).
  const [orderColumnSelection, setOrderColumnSelection] = useState<ReadonlySet<string>>(new Set())
  // PROD-1335 — one Orders sheet per bucket instead of one combined sheet.
  // Off by default: the combined sheet is the cheaper artifact, and only
  // the pass-out-by-classroom workflow wants N sheets.
  const [splitOrdersByBucket, setSplitOrdersByBucket] = useState(false)

  // Presets anchor on the campus clock once history delivers the timezone;
  // until then the browser's day stands in (same day for almost everyone).
  const browserToday = useMemo(() => campusTodayIso(Intl.DateTimeFormat().resolvedOptions().timeZone), [])
  const [campusToday, setCampusToday] = useState<string | null>(null)
  const anchor = campusToday ?? browserToday
  const presets = useMemo(() => schoolPrintPresets(anchor), [anchor])

  const range: SchoolPrintRange | null = useMemo(() => {
    if (preset === 'custom') {
      return customFrom && customTo ? clampCustomRange(customFrom, customTo) : null
    }
    return presets[preset]
  }, [preset, presets, customFrom, customTo])

  const historyQuery = useServiceHistoryQuery(
    locationId && range ? { schoolLocationId: locationId, from: range.from, to: range.to } : null,
  )
  const history = historyQuery.data ?? null
  // One-time anchor refinement — only when the campus calendar disagrees
  // with the browser's (roaming laptop, cross-country admin).
  const campusTz = history?.location.timeZone
  useEffect(() => {
    if (campusTz) {
      const campusIso = campusTodayIso(campusTz)
      setCampusToday((prev) => prev ?? campusIso)
    }
  }, [campusTz])

  const productionRecordsEnabled = history?.productionRecordsEnabled ?? true
  const recordsAvailable = !hideRecordsSection && productionRecordsEnabled

  const dayOptions: SchoolPrintDayOption[] = useMemo(
    () => (history && range ? buildSchoolPrintDayOptions(history.days, range.days) : []),
    [history, range],
  )
  const selectedDays = useMemo(
    () => dayOptions.filter((option) => option.hasService && !excludedDays.has(option.iso)).map((o) => o.iso),
    [dayOptions, excludedDays],
  )

  // Every mealtime with a record or a pass-out sheet on the selected days;
  // the pills, the counts and the request all read the same selection.
  const mealtimeOptions: SchoolPrintMealtimeOption[] = useMemo(
    () => (history ? buildSchoolPrintMealtimeOptions(history.days, selectedDays) : []),
    [history, selectedDays],
  )
  const selectedMealtimes = useMemo(
    () => mealtimeOptions.filter((option) => !excludedMealtimes.has(option.mealtime)).map((option) => option.mealtime),
    [mealtimeOptions, excludedMealtimes],
  )
  const selectedMealtimeSet = useMemo(() => new Set<Mealtime>(selectedMealtimes), [selectedMealtimes])
  // A proper subset only — every mealtime picked is the same packet as no
  // filter, so the URL stays clean (and behavior-identical) in that case.
  const mealtimeFilter = useMemo(
    () =>
      selectedMealtimes.length < mealtimeOptions.length ? new Set<Mealtime>(selectedMealtimes) : new Set<Mealtime>(),
    [selectedMealtimes, mealtimeOptions],
  )

  const deadlinePolicy = useMemo(() => (history ? cafeteriaDeadlinePolicy(history.location) : null), [history])
  const unconfirmedDays = useMemo(
    () => selectedDays.filter((iso) => !ordersConfirmedForDay(iso, deadlinePolicy)),
    [selectedDays, deadlinePolicy],
  )

  // Orders-sheet counts: combined school + parent demand per day, from the
  // same month payloads (and cache keys) the week views read. A ≤14-day
  // range spans at most two calendar months.
  const ordersMonths = useMemo(() => {
    if (!range) {
      return []
    }
    const seen = new Map<string, { year: number; month: number }>()
    for (const iso of range.days) {
      const key = iso.slice(0, 7)
      if (!seen.has(key)) {
        seen.set(key, { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) })
      }
    }
    return [...seen.values()]
  }, [range])
  const ordersQueries = useQueries({
    queries: (locationId ? ordersMonths : []).map((m) => ({
      queryKey: ['school-orders', 'forecast-batches', { schoolLocationId: locationId ?? '', ...m }],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchSchoolForecastBatchesForMonth({ schoolLocationId: locationId ?? '', ...m }, signal),
      staleTime: 15 * 1000,
    })),
    combine: (results) => ({
      batches: results.flatMap((result) => result.data?.schoolForecastBatchesForMonth ?? []),
      familyOrders: results.flatMap((result) => result.data?.familyOrders ?? []),
    }),
  })
  // Day-level presence only (dayQty), which is bucketing-independent — no
  // school shape needed here.
  const ordersLookup = useMemo(
    () =>
      buildAlreadyOrdered(ordersQueries.batches, {
        familyOrders: ordersQueries.familyOrders,
        combineFamilyIntoTotals: true,
      }),
    [ordersQueries.batches, ordersQueries.familyOrders],
  )

  // Bucket options for the Orders filter chips — the same campus-shape query
  // (and cache key) the print page resolves its columns from, fetched only
  // once the Orders section is actually on. The chips can therefore only
  // ever offer exactly the buckets the sheet renders.
  const ordersSchoolQuery = useMySchoolAdminLocationsQuery('CAFETERIA_WORKER', locationId, {
    enabled: Boolean(locationId) && sections.orders,
  })
  const ordersSchool = useMemo(
    () => (ordersSchoolQuery.data?.me?.schoolAdminLocations ?? []).find((s) => s.id === locationId) ?? null,
    [ordersSchoolQuery.data, locationId],
  )
  const orderColumnOptions = useMemo(
    () =>
      ordersSchool
        ? buildSchoolPrintOrderColumns(ordersSchool).map((col) => ({ id: col.columnId, label: col.label }))
        : [],
    [ordersSchool],
  )
  /**
   * What the campus calls one Orders column, lowercased for mid-sentence
   * use ("One sheet per homeroom"). A HOMEROOM campus reads "homeroom", a
   * ROOM campus "classroom" — the dialog must not tell a room-grouped
   * campus about homerooms it does not have. Falls back to the neutral
   * "group" for AGE_GROUP / unresolved campuses.
   */
  const orderBucketNoun =
    ordersSchool && ordersSchool.orderGroupBy in ENROLLMENT_FACT_LABELS
      ? ENROLLMENT_FACT_LABELS[ordersSchool.orderGroupBy as keyof typeof ENROLLMENT_FACT_LABELS].toLowerCase()
      : 'group'
  // Validated against the options so a stale pick (campus columns changed
  // under an open dialog) never reaches the URL.
  const selectedOrderColumnIds = useMemo(
    () => orderColumnOptions.filter((option) => orderColumnSelection.has(option.id)).map((option) => option.id),
    [orderColumnOptions, orderColumnSelection],
  )

  const counts = useMemo(() => {
    const orders = selectedDays.filter((iso) => ordersLookup.dayQty(iso) > 0).length
    if (!history) {
      return { records: 0, filled: 0, passout: 0, orders }
    }
    const byIso = new Map(history.days.map((day) => [day.serviceDate, day] as const))
    let records = 0
    let filled = 0
    let passout = 0
    for (const iso of selectedDays) {
      const day = byIso.get(iso)
      if (!day) {
        continue
      }
      const dayRecords = day.productionRecords.filter((r) => selectedMealtimeSet.has(r.mealtime))
      records += dayRecords.length
      filled += dayRecords.filter((r) => r.status === 'SUBMITTED' || r.status === 'AMENDED').length
      passout += day.mealtimes.filter((m) => selectedMealtimeSet.has(m.mealtime)).length
    }
    return { records, filled, passout, orders }
  }, [history, selectedDays, ordersLookup, selectedMealtimeSet])

  // A split day prints up to one sheet per bucket in scope (buckets with no
  // orders that day drop out at build time, so this is an upper bound — the
  // dialog says "up to" rather than promising a number it cannot know
  // without per-bucket demand, which the dialog deliberately does not fetch).
  const orderBucketsInScope =
    selectedOrderColumnIds.length > 0 ? selectedOrderColumnIds.length : orderColumnOptions.length
  const orderSheetCount =
    splitOrdersByBucket && orderBucketsInScope > 0 ? counts.orders * orderBucketsInScope : counts.orders

  const documentCount =
    (sections.records && recordsAvailable ? counts.records : 0) +
    (sections.passout ? counts.passout : 0) +
    (sections.orders ? orderSheetCount : 0)

  // Roster vs tally over the SELECTED days — drives the pass-out section's
  // title, copy, and whether roster-only affordances (grouping, privacy note)
  // appear at all.
  const composition = useMemo(
    () => (history ? passoutComposition(history.days, selectedDays) : ('unknown' as const)),
    [history, selectedDays],
  )
  const passoutHasRoster = composition === 'parent' || composition === 'mixed'

  const serveGroupBy = history?.location.serveGroupBy ?? null
  const rosterAxis = defaultRosterGrouping(serveGroupBy) === 'homeroom' ? 'homeroom' : 'grade'
  const rosterNoun = rosterAxis === 'homeroom' ? homeroomFacetLabel(serveGroupBy).toLowerCase() : 'grade'
  const rosterSessions = useMemo(
    () =>
      (history?.days ?? [])
        .filter((day) => selectedDays.includes(day.serviceDate))
        .flatMap((day) =>
          day.mealtimes
            .filter((mealtime) => mealtime.hasParentOrders && selectedMealtimeSet.has(mealtime.mealtime))
            .map(({ mealtime }) => ({ serviceDate: day.serviceDate, mealtime })),
        ),
    [history, selectedDays, selectedMealtimeSet],
  )
  const roster = useSchoolPrintRoster({
    locationId,
    axis: rosterAxis,
    sessions: rosterSessions,
    enabled: open && sections.passout && passoutHasRoster,
  })
  const grouping = roster.grouped ? rosterAxis : 'alpha'

  const notMaterializedCount = useMemo(
    () => dayOptions.filter((option) => selectedDays.includes(option.iso) && option.notMaterialized).length,
    [dayOptions, selectedDays],
  )

  // The one selection both consumers read: Print opens `/school/print` with
  // it encoded in the URL; Download as CSV assembles the same packet in
  // place. Every "only when it matters" rule lives here, so the URL and the
  // in-place job can never describe different selections.
  const printRequest = useMemo<SchoolPrintRequest | null>(() => {
    if (sections.passout && passoutHasRoster && roster.filter && roster.labels.size === 0) {
      return null
    }
    if (!locationId || !range || selectedDays.length === 0 || documentCount === 0) {
      return null
    }
    const chosen = new Set<SchoolPrintSectionKey>()
    if (sections.records && recordsAvailable) {
      chosen.add('records')
    }
    if (sections.passout) {
      chosen.add('passout')
    }
    if (sections.orders) {
      chosen.add('orders')
    }
    // The range is the SELECTED span — its `days` are the page's default
    // when the subset needs no spelling out.
    const selectedRange = clampCustomRange(selectedDays[0]!, selectedDays[selectedDays.length - 1]!)
    if (!selectedRange) {
      return null
    }
    // A proper subset only — every chip picked is the same sheet as no
    // filter, so the URL stays clean (and behavior-identical) in that case.
    const orderColumnFilter =
      sections.orders && selectedOrderColumnIds.length > 0 && selectedOrderColumnIds.length < orderColumnOptions.length
        ? new Set(selectedOrderColumnIds)
        : new Set<string>()
    return {
      locationId,
      range: selectedRange,
      selectedDays,
      sections: chosen,
      // Always explicit on a roster (PROD-1234): the page's no-param default
      // is the LEGACY campus bucket, kept only for pre-split links, and "Per
      // grade" on a homeroom-axis campus is a real choice the URL has to
      // carry. A tally-only selection prints per bucket regardless.
      grouping: sections.passout && passoutHasRoster ? grouping : 'buckets',
      ...(sections.passout && passoutHasRoster && roster.filter ? { rosterFilter: roster.filter } : {}),
      mealtimeFilter,
      orderColumnFilter,
      // Only meaningful with more than one bucket — splitting a single-column
      // campus produces the same one sheet, so the URL stays clean.
      splitOrdersByBucket: sections.orders && splitOrdersByBucket && orderColumnOptions.length > 1,
    }
  }, [
    locationId,
    range,
    selectedDays,
    documentCount,
    sections,
    recordsAvailable,
    grouping,
    roster.filter,
    roster.labels.size,
    passoutHasRoster,
    mealtimeFilter,
    selectedOrderColumnIds,
    orderColumnOptions,
    splitOrdersByBucket,
  ])
  const printUrl = useMemo(() => (printRequest ? schoolPrintRequestUrl(printRequest) : null), [printRequest])

  function toggleMealtime(mealtime: Mealtime): void {
    setExcludedMealtimes((prev) => {
      const next = new Set(prev)
      if (next.has(mealtime)) {
        next.delete(mealtime)
      } else {
        next.add(mealtime)
      }
      return next
    })
  }

  function selectAllMealtimes(): void {
    setExcludedMealtimes(new Set())
  }

  function toggleDay(iso: string): void {
    setExcludedDays((prev) => {
      const next = new Set(prev)
      if (next.has(iso)) {
        next.delete(iso)
      } else {
        next.add(iso)
      }
      return next
    })
  }

  function toggleOrderColumn(id: string): void {
    setOrderColumnSelection((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function clearOrderColumns(): void {
    setOrderColumnSelection(new Set())
  }

  function selectPreset(next: SchoolPrintPreset): void {
    setPreset(next)
    setExcludedDays(new Set())
    setExcludedMealtimes(new Set())
  }

  return {
    preset,
    selectPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    range,
    dayOptions,
    selectedDays,
    toggleDay,
    mealtimeOptions,
    selectedMealtimes,
    toggleMealtime,
    selectAllMealtimes,
    sections,
    setSections,
    grouping,
    roster,
    rosterNoun,
    orderColumnOptions,
    selectedOrderColumnIds,
    toggleOrderColumn,
    clearOrderColumns,
    splitOrdersByBucket,
    setSplitOrdersByBucket,
    orderBucketNoun,
    orderSheetCount,
    passoutComposition: composition,
    passoutHasRoster,
    recordsAvailable,
    counts,
    documentCount,
    unconfirmedDays,
    notMaterializedCount,
    historyPending: historyQuery.isPending && Boolean(locationId && range),
    historyError: historyQuery.isError,
    printRequest,
    printUrl,
  }
}
